import { useEffect, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { useMutation } from '@apollo/client'
import { XMarkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { COMPLETE_INCOMING_ORDER_EXTERNALLY } from '../graphql/mutations'
import { Order } from '../types/order'

const ORDERLINE_URL = 'https://orderline.hubrise-apps.com/orders?app_instance_id=yyn65'

interface HandleExternallyModalProps {
  isOpen: boolean
  orderId: number
  orderNumber: string
  customerName?: string
  onClose: () => void
  onCompleted: () => void
}

const HandleExternallyModal: React.FC<HandleExternallyModalProps> = ({
  isOpen,
  orderId,
  orderNumber,
  customerName,
  onClose,
  onCompleted,
}) => {
  const [reason, setReason] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)

  const [runMutation, { loading }] = useMutation<{ completeIncomingOrderExternally: Order }>(
    COMPLETE_INCOMING_ORDER_EXTERNALLY,
  )

  useEffect(() => {
    if (!isOpen) {
      setReason('')
      setSubmitError(null)
    }
  }, [isOpen])

  const handleSubmit = async () => {
    setSubmitError(null)
    try {
      await runMutation({
        variables: {
          orderId,
          reason: reason.trim() === '' ? null : reason.trim(),
        },
      })
      onCompleted()
      onClose()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to mark order completed.'
      setSubmitError(message)
    }
  }

  return (
    <Transition appear show={isOpen}>
      <Dialog as="div" className="relative z-[55]" onClose={loading ? () => {} : onClose}>
        <Transition.Child
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-start justify-between mb-3">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Handle order {orderNumber} in OrderLine
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    disabled={loading}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                    aria-label="Close"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3">
                  <p className="text-sm text-amber-900">
                    We couldn't fully ingest this HubRise order into the POS. Prepare it from the
                    OrderLine app instead — don't process it in this register.
                  </p>
                </div>

                <ol className="mt-4 space-y-3 text-sm text-gray-800">
                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-800 text-xs font-semibold">
                      1
                    </span>
                    <div className="flex-1">
                      <p>Open the OrderLine app in a new tab.</p>
                      <a
                        href={ORDERLINE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1.5 inline-flex items-center gap-1.5 rounded-md border border-primary-200 bg-white px-3 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-50"
                      >
                        Open OrderLine
                        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                      </a>
                    </div>
                  </li>

                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-800 text-xs font-semibold">
                      2
                    </span>
                    <div className="flex-1">
                      <p>
                        Find the order for{' '}
                        <span className="font-semibold text-gray-900">
                          {customerName || '(no customer name)'}
                        </span>
                        . Its status in OrderLine should still be <em>received</em>. <strong>Only handle this one.</strong>
                      </p>
                    </div>
                  </li>

                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-800 text-xs font-semibold">
                      3
                    </span>
                    <div className="flex-1">
                      <p>Print or write out the receipt and hand it to the kitchen — they'll prepare it separately.</p>
                    </div>
                  </li>

                  <li className="flex gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-800 text-xs font-semibold">
                      4
                    </span>
                    <div className="flex-1">
                      <p>
                        Once done, mark this order completed here. It will be removed from the queue and
                        the order moves to <em>accepted</em> in HubRise; you finish the remaining statuses in OrderLine.
                      </p>
                    </div>
                  </li>
                </ol>

                <label className="block mt-4 text-sm">
                  <span className="text-gray-700 font-medium">
                    Note <span className="text-gray-400 font-normal">(optional)</span>
                  </span>
                  <input
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    disabled={loading}
                    placeholder="e.g. unknown menu item, catalog drift"
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100"
                  />
                </label>

                {submitError && (
                  <div className="mt-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                    {submitError}
                  </div>
                )}

                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
                  >
                    {loading ? 'Marking…' : 'Mark completed here'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default HandleExternallyModal
